(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const openDialog = (dialog) => {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  };

  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  const animateDisclosure = (details, shouldOpen) => {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;

    const previous = details._ptDisclosureAnimation;
    if (previous) previous.cancel();

    if (reducedMotion()) {
      details.open = shouldOpen;
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
      details.removeAttribute('data-pt-animating');
      return;
    }

    const startHeight = details.getBoundingClientRect().height;
    if (shouldOpen) details.open = true;
    const endHeight = shouldOpen ? details.scrollHeight : summary.getBoundingClientRect().height;

    details.style.height = startHeight + 'px';
    details.style.overflow = 'hidden';
    details.setAttribute('data-pt-animating', shouldOpen ? 'opening' : 'closing');

    const animation = details.animate(
      { height: [startHeight + 'px', endHeight + 'px'] },
      { duration: 380, easing: 'cubic-bezier(.2,.8,.2,1)' },
    );
    details._ptDisclosureAnimation = animation;

    animation.onfinish = () => {
      if (!shouldOpen) details.open = false;
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
      details.removeAttribute('data-pt-animating');
      details._ptDisclosureAnimation = null;
    };
    animation.oncancel = () => {
      details._ptDisclosureAnimation = null;
    };
  };

  const initReviewDots = (row, cards) => {
    const section = row.closest('section');
    const dots = [...(section?.querySelectorAll('.rev-dot') || [])];
    const setActiveDot = () => {
      if (!dots.length || !cards.length) return;
      const step = cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
      const cardIndex = Math.round(row.scrollLeft / Math.max(step, 1)) % cards.length;
      const pageIndex = Math.min(dots.length - 1, Math.floor(cardIndex / 3));
      dots.forEach((dot, index) => dot.classList.toggle('on', index === pageIndex));
    };

    dots.forEach((dot, index) => {
      dot.type = 'button';
      dot.addEventListener('click', () => {
        const target = cards[Math.min(index * 3, cards.length - 1)];
        target?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
        dots.forEach((item) => item.classList.toggle('on', item === dot));
        row.dispatchEvent(new CustomEvent('pt:carousel-interaction'));
      });
    });
    row.addEventListener('scroll', setActiveDot, { passive: true });
  };

  const initDesktopCarousel = (scroller, itemSelector, interval) => {
    const desktop = matchMedia('(min-width: 901px)');
    const originals = [...scroller.querySelectorAll(itemSelector)];
    if (!originals.length) return;

    initReviewDots(scroller, originals);
    if (!desktop.matches) return;

    const step = () => originals[1]
      ? originals[1].offsetLeft - originals[0].offsetLeft
      : originals[0].offsetWidth;
    const visibleCount = Math.min(originals.length, Math.ceil(scroller.clientWidth / Math.max(step(), 1)) + 1);
    const clones = originals.slice(0, visibleCount).map((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      item.parentElement.append(clone);
      return clone;
    });

    let timer = 0;
    let resetTimer = 0;
    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const boundary = () => clones[0].offsetLeft - originals[0].offsetLeft;
    const stop = () => {
      window.clearTimeout(timer);
      window.clearTimeout(resetTimer);
    };
    const schedule = (delay = interval) => {
      window.clearTimeout(timer);
      if (reducedMotion()) return;
      timer = window.setTimeout(advance, delay);
    };
    const normalize = () => {
      const loopAt = boundary();
      if (loopAt > 0 && scroller.scrollLeft >= loopAt - 2) {
        scroller.scrollTo({ left: Math.max(0, scroller.scrollLeft - loopAt), behavior: 'auto' });
      }
    };
    const advance = () => {
      if (document.hidden || pointerId !== null) {
        schedule(1200);
        return;
      }
      const loopAt = boundary();
      const next = scroller.scrollLeft + step();
      scroller.scrollTo({ left: Math.min(next, loopAt), behavior: 'smooth' });
      if (next >= loopAt - 2) resetTimer = window.setTimeout(normalize, 700);
      schedule();
    };

    scroller.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      stop();
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = scroller.scrollLeft;
      moved = false;
      scroller.classList.add('is-dragging');
      scroller.style.scrollSnapType = 'none';
      scroller.setPointerCapture?.(event.pointerId);
    });
    scroller.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) moved = true;
      scroller.scrollLeft = startScroll - delta;
      if (moved) event.preventDefault();
    });
    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      scroller.releasePointerCapture?.(event.pointerId);
      pointerId = null;
      scroller.classList.remove('is-dragging');
      scroller.style.removeProperty('scroll-snap-type');
      window.setTimeout(normalize, 450);
      schedule(2800);
    };
    scroller.addEventListener('pointerup', endDrag);
    scroller.addEventListener('pointercancel', endDrag);
    scroller.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
    scroller.addEventListener('dragstart', (event) => event.preventDefault());
    scroller.addEventListener('pt:carousel-interaction', () => schedule(3500));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else schedule(1200);
    });
    schedule();
  };

  ready(() => {
    const body = document.body;

    const menu = document.querySelector('[data-pt-mobile-menu]');
    const menuOpen = document.querySelector('[data-pt-menu-open]');
    const menuClose = menu?.querySelector('[data-pt-menu-close]');
    let menuTrigger = null;
    if (menu && menuOpen) {
      menuOpen.addEventListener('click', () => {
        menuTrigger = document.activeElement;
        openDialog(menu);
        menuClose?.focus({ preventScroll: true });
      });
      menuClose?.addEventListener('click', () => menu.close());
      menu.addEventListener('click', (event) => {
        if (event.target === menu) menu.close();
      });
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => menu.close()));
      menu.addEventListener('close', () => menuTrigger?.focus());
    }

    const navDropdowns = [...document.querySelectorAll('.pt-site-header .pt-nav-dropdown')];
    if (matchMedia('(min-width: 981px) and (hover: hover) and (pointer: fine)').matches) {
      navDropdowns.forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        let closeTimer = 0;
        const setOpen = (open) => {
          window.clearTimeout(closeTimer);
          if (open) navDropdowns.forEach((other) => {
            if (other !== details) other.removeAttribute('open');
          });
          details.open = open;
          summary?.setAttribute('aria-expanded', String(open));
        };
        details.addEventListener('mouseenter', () => setOpen(true));
        details.addEventListener('mouseleave', () => {
          closeTimer = window.setTimeout(() => setOpen(false), 180);
        });
        details.addEventListener('focusin', () => setOpen(true));
        details.addEventListener('focusout', () => {
          closeTimer = window.setTimeout(() => {
            if (!details.contains(document.activeElement)) setOpen(false);
          }, 0);
        });
        summary?.addEventListener('click', (event) => {
          const landingUrl = summary.dataset.ptNavUrl;
          if (!landingUrl) return;
          event.preventDefault();
          location.assign(landingUrl);
        });
        details.addEventListener('toggle', () => summary?.setAttribute('aria-expanded', String(details.open)));
      });
    }

    document.addEventListener('click', (event) => {
      document.querySelectorAll('.pt-site-header details[open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    });

    document.querySelectorAll('details.pt-disclosure').forEach((item) => {
      const siblings = [...item.parentElement.children].filter((element) => element.matches('details.pt-disclosure'));
      const summary = item.querySelector(':scope > summary');
      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        const animationTarget = item.getAttribute('data-pt-animating');
        const shouldOpen = animationTarget ? animationTarget !== 'opening' : !item.open;
        const isExclusiveFaq = Boolean(item.closest('#faq, .faq-row'));
        if (shouldOpen && isExclusiveFaq) siblings.forEach((other) => {
          if (other !== item && (other.open || other.hasAttribute('data-pt-animating'))) {
            animateDisclosure(other, false);
          }
        });
        animateDisclosure(item, shouldOpen);
      });
    });

    document.querySelectorAll('[data-pt-map]').forEach((map) => {
      if (map.querySelector('iframe')) return;
      const iframe = document.createElement('iframe');
      iframe.src = map.dataset.mapSrc;
      iframe.title = map.dataset.mapTitle || '';
      iframe.referrerPolicy = 'no-referrer-when-downgrade';
      iframe.allowFullscreen = true;
      map.prepend(iframe);
      map.querySelector('.pt-map__placeholder')?.remove();
    });

    document.querySelectorAll('[data-pt-phone]').forEach((phone) => {
      const validate = () => {
        const digits = phone.value.replace(/\D/g, '');
        phone.setCustomValidity(phone.value && digits.length < 9 ? phone.dataset.invalidMessage : '');
      };
      phone.addEventListener('input', validate);
      phone.addEventListener('invalid', validate);
    });

    const status = document.querySelector('[data-pt-form-status]');
    if (status) {
      status.focus({ preventScroll: true });
      status.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    document.querySelectorAll('.marquee').forEach((scroller) => {
      initDesktopCarousel(scroller, '.marquee-item', 3600);
    });
    document.querySelectorAll('.rev-row').forEach((scroller) => {
      initDesktopCarousel(scroller, '.rev-card', 4400);
    });

    const mobileBar = document.querySelector('[data-pt-mobile-bar]');
    if (mobileBar && matchMedia('(max-width: 900px)').matches) {
      const pathname = location.pathname.replace(/^\/en(?=\/|$)/, '') || '/';
      const isHomepage = pathname === '/';
      const isLocalCoverage = Boolean(document.querySelector('.loc-hero .loc-cta'));
      const heroActions = isHomepage
        ? document.querySelector('.hero .hero-cta')
        : (isLocalCoverage ? document.querySelector('.loc-hero .loc-cta') : null);
      const setMobileBar = (show) => mobileBar.classList.toggle('on', show);

      if (heroActions && 'IntersectionObserver' in window) {
        setMobileBar(false);
        const observer = new IntersectionObserver((entries) => {
          setMobileBar(!entries[0].isIntersecting);
        }, { threshold: 0 });
        observer.observe(heroActions);
      } else {
        setMobileBar(true);
      }
    }

    if ('IntersectionObserver' in window && matchMedia('(max-width: 900px)').matches) {
      const cards = document.querySelectorAll('.svc-io');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('active', entry.isIntersecting));
      }, { rootMargin: '-35% 0px -35% 0px', threshold: 0 });
      cards.forEach((card) => observer.observe(card));
    }

    const galleryButtons = [...document.querySelectorAll('button.gph')];
    if (galleryButtons.length) {
      const dialog = document.createElement('dialog');
      dialog.className = 'pt-lightbox';
      dialog.innerHTML =
        '<button type="button" class="pt-lightbox__close" data-lightbox-close aria-label="' + (body.dataset.ptClose || 'Close') + '">×</button>' +
        '<button type="button" class="pt-lightbox__arrow pt-lightbox__arrow--prev" data-lightbox-prev aria-label="' + (body.dataset.ptPrevious || 'Previous') + '"><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M15 19 8 12l7-7" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<figure><img alt=""><figcaption></figcaption></figure>' +
        '<button type="button" class="pt-lightbox__arrow pt-lightbox__arrow--next" data-lightbox-next aria-label="' + (body.dataset.ptNext || 'Next') + '"><svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
      body.append(dialog);
      const image = dialog.querySelector('img');
      const caption = dialog.querySelector('figcaption');
      let current = 0;
      let trigger = null;
      const show = (index) => {
        current = (index + galleryButtons.length) % galleryButtons.length;
        const source = galleryButtons[current].querySelector('img');
        image.src = source.dataset.full || source.currentSrc || source.src;
        image.alt = source.alt || '';
        caption.textContent = source.alt || '';
      };
      galleryButtons.forEach((button, index) => {
        button.type = 'button';
        const source = button.querySelector('img');
        if (!button.hasAttribute('aria-label')) button.setAttribute('aria-label', source?.alt || String(index + 1));
        button.addEventListener('click', () => {
          trigger = button;
          show(index);
          openDialog(dialog);
        });
      });
      dialog.querySelector('[data-lightbox-close]').addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(current - 1));
      dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => show(current + 1));
      let suppressBackdropClickUntil = 0;
      dialog.addEventListener('click', (event) => {
        if (performance.now() <= suppressBackdropClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') show(current - 1);
        if (event.key === 'ArrowRight') show(current + 1);
      });
      const figure = dialog.querySelector('figure');
      let swipePointer = null;
      let swipeStartX = 0;
      let swipeStartY = 0;
      let swipeDeltaX = 0;
      let swipeResetTimer = 0;
      const clearSwipeStyles = () => {
        window.clearTimeout(swipeResetTimer);
        figure.style.removeProperty('transition');
        figure.style.removeProperty('transform');
        figure.style.removeProperty('opacity');
      };
      const returnSwipeToCenter = () => {
        figure.style.transition = reducedMotion() ? 'none' : 'transform 180ms var(--ease-out), opacity 180ms var(--ease-out)';
        figure.style.transform = 'translate3d(0,0,0)';
        figure.style.opacity = '1';
        swipeResetTimer = window.setTimeout(clearSwipeStyles, reducedMotion() ? 0 : 190);
      };
      const completeSwipe = (direction) => {
        const travel = Math.min(170, Math.max(84, figure.getBoundingClientRect().width * .16));
        figure.style.transition = reducedMotion() ? 'none' : 'transform 150ms cubic-bezier(.4,0,1,1), opacity 150ms linear';
        figure.style.transform = 'translate3d(' + (direction > 0 ? -travel : travel) + 'px,0,0)';
        figure.style.opacity = '0';
        swipeResetTimer = window.setTimeout(() => {
          show(current + direction);
          figure.style.transition = 'none';
          figure.style.transform = 'translate3d(' + (direction > 0 ? travel * .55 : -travel * .55) + 'px,0,0)';
          requestAnimationFrame(() => requestAnimationFrame(() => {
            figure.style.transition = reducedMotion() ? 'none' : 'transform 220ms var(--ease-out), opacity 220ms var(--ease-out)';
            figure.style.transform = 'translate3d(0,0,0)';
            figure.style.opacity = '1';
            swipeResetTimer = window.setTimeout(clearSwipeStyles, reducedMotion() ? 0 : 230);
          }));
        }, reducedMotion() ? 0 : 150);
      };
      figure.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        window.clearTimeout(swipeResetTimer);
        swipePointer = event.pointerId;
        swipeStartX = event.clientX;
        swipeStartY = event.clientY;
        swipeDeltaX = 0;
        figure.style.transition = 'none';
        figure.setPointerCapture?.(event.pointerId);
      });
      figure.addEventListener('pointermove', (event) => {
        if (event.pointerId !== swipePointer) return;
        const deltaX = event.clientX - swipeStartX;
        const deltaY = event.clientY - swipeStartY;
        if (Math.abs(deltaX) < Math.abs(deltaY) && Math.abs(deltaX) < 12) return;
        event.preventDefault();
        swipeDeltaX = deltaX;
        const visualDelta = Math.max(-150, Math.min(150, deltaX * .58));
        figure.style.transform = 'translate3d(' + visualDelta + 'px,0,0)';
        figure.style.opacity = String(1 - Math.min(Math.abs(visualDelta) / 620, .22));
      }, { passive: false });
      const endSwipe = (event, cancelled = false) => {
        if (event.pointerId !== swipePointer) return;
        figure.releasePointerCapture?.(event.pointerId);
        swipePointer = null;
        if (cancelled || Math.abs(swipeDeltaX) < 44) {
          returnSwipeToCenter();
          return;
        }
        suppressBackdropClickUntil = performance.now() + 650;
        completeSwipe(swipeDeltaX < 0 ? 1 : -1);
      };
      figure.addEventListener('pointerup', (event) => endSwipe(event));
      figure.addEventListener('pointercancel', (event) => endSwipe(event, true));
      dialog.addEventListener('dragstart', (event) => event.preventDefault());
      dialog.addEventListener('close', () => {
        swipePointer = null;
        clearSwipeStyles();
        trigger?.focus();
      });
    }
  });
})();
